import React, { useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../styles/quill-custom.css';
import LinkPreviewSection from './LinkPreviewSection';
import FileAttachmentSection from './FileAttachmentSection';
import { urlPreviewUtils } from '../utils/urlPreview';

/**
 * Email Template Form Fields Component
 * Contains form fields specifically for email templates
 */
const EmailTemplateFormFields = ({
  // Form data
  formData,
  onInputChange,

  // Template type selection (for templates)
  showTemplateHeader = false,
  showTemplateType = true,
  templateTypes = ['Cold Leads', 'Warm Prospects', 'Active Clients'],

  // Link previews
  linkPreviews = {},
  loadingPreview = false,
  setLinkPreviews,
  setLoadingPreview,

  // File attachments
  attachments = [],
  uploadingFile = false,
  onFileSelect,
  onRemoveAttachment,

  // Styling
  compact = false,
  disabled = false,

  // Template read-only mode
  templateReadOnly = false,
  editableBody = false
}) => {

  // React Quill configuration - minimal toolbar for email templates
  const quillModules = {
    toolbar: [
      ['bold'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean'] // remove formatting button
    ]
  };

  const quillFormats = [
    'bold', 'list', 'bullet', 'link'
  ];

  // Handle body content change with link preview detection
  const handleBodyChange = useCallback(async (content) => {
    // Update form data
    onInputChange({ target: { name: 'body', value: content } });

    // Detect and preview URLs if setters are provided
    if (setLinkPreviews && setLoadingPreview && content) {
      await urlPreviewUtils.detectAndPreviewUrls(content, linkPreviews, setLinkPreviews, setLoadingPreview);
    }
  }, [onInputChange, linkPreviews, setLinkPreviews, setLoadingPreview]);

  return (
    <div className="space-y-4">
      {/* Template Name */}
      {showTemplateHeader && (<div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Template Name
        </label>
        <input
          type="text"
          name="name"
          value={formData.name || ''}
          onChange={onInputChange}
          className="w-full p-3 border border-gray-300 rounded-lg"
          placeholder="e.g., Cold Lead - Follow up #2"
          required
          autoComplete="off"
          disabled={disabled}
        />
      </div>
      )}

      {/* Subject Line */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Subject Line {templateReadOnly && <span className="text-xs text-gray-500">(from template)</span>}
        </label>
        <input
          type="text"
          name="subject"
          value={formData.subject || ''}
          onChange={onInputChange}
          className={`w-full p-3 border rounded-lg ${templateReadOnly ? 'border-gray-200 bg-gray-50 text-gray-600' : 'border-gray-300'}`}
          placeholder="Use {FIRSTNAME}, {COMPANY}, {JOB_TITLE} for personalization"
          required
          autoComplete="off"
          disabled={disabled || templateReadOnly}
          readOnly={templateReadOnly}
        />
      </div>

      {/* Email Body */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email Body {templateReadOnly && (
            <span className="text-xs text-gray-500">
              {editableBody ? '(editing shared template)' : '(from template)'}
            </span>
          )}
        </label>
        <div className={`${templateReadOnly && !editableBody ? 'opacity-60' : ''}`}>
          <ReactQuill
            value={formData.body || ''}
            onChange={handleBodyChange}
            modules={quillModules}
            formats={quillFormats}
            placeholder="Hi {FIRSTNAME},

I noticed {COMPANY} is doing interesting work...

Best regards,"
            readOnly={disabled || (templateReadOnly && !editableBody)}
            className={`${templateReadOnly && !editableBody ? 'read-only-editor' : ''}`}
          />
        </div>

        {/* Link Preview Toggle */}
        <div className="flex items-center mt-3">
          <input
            type="checkbox"
            id="linkPreview"
            name="linkPreview"
            checked={formData.linkPreviewToggle || false}
            onChange={(e) => onInputChange({ target: { name: 'linkPreviewToggle', value: e.target.checked } })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            disabled={disabled || templateReadOnly}
          />
          <label htmlFor="linkPreview" className="ml-2 block text-sm text-gray-700">
            Enable link previews {templateReadOnly && <span className="text-xs text-gray-500">(from template)</span>}
          </label>
        </div>

        {/* Link Preview Section */}
        {formData.linkPreviewToggle && (
          <LinkPreviewSection
            linkPreviews={linkPreviews}
            loadingPreview={loadingPreview}
            compact={compact}
            readOnly={templateReadOnly}
          />
        )}
      </div>

      {/* File Attachments Section */}
      <FileAttachmentSection
        attachments={attachments}
        uploadingFile={uploadingFile}
        onFileSelect={onFileSelect}
        onRemoveAttachment={onRemoveAttachment}
        disabled={disabled}
        compact={compact}
        readOnly={templateReadOnly}
      />
    </div>
  );
};

export default EmailTemplateFormFields;
